import { useMoveFileRecord, workspaceTreeQuery } from "@/api/fileRecords";
import { trackActionInvoked } from "@/object-actions/trackObjectAction";
import type {
  ObjectActionDestinationSubmenu,
  ResolvedObjectAction,
  TransferDestination,
} from "@/object-actions/types";
import { useFolderCascade } from "@/pages/workspaces/hooks/useFolderCascade";
import type { UnifiedItem } from "@/pages/workspaces/types";
import type { Canvas, FileRecord } from "@cohesive/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import toast from "react-hot-toast";
import { leafNameFromPath } from "./paths";
import { useWritableWorkspaceIds } from "./useWritableWorkspaces";

/**
 * Bulk Move to for the multi-selection menu. Canvases and files share the
 * workspace tree, so one folder picker serves any selection: files and
 * folders cascade through the file-record move, canvases re-file through the
 * canvas API. Commits run sequentially and report one toast.
 */
export function useAttachBulkTransferSubmenu(
  actions: ResolvedObjectAction[],
  items: UnifiedItem[],
  workspaceId: string | undefined,
  workspaceFileRecords: FileRecord[],
  onMoved?: () => void,
): ResolvedObjectAction[] {
  const moveFileRecord = useMoveFileRecord(
    workspaceId ? { class: "workspace", workspace_id: workspaceId } : undefined,
  );
  const cascade = useFolderCascade();
  const queryClient = useQueryClient();

  const hasBulkMove = actions.some((a) => a.id === "bulk-move");
  const writableWorkspaceIds = useWritableWorkspaceIds(hasBulkMove);

  const commit = useCallback(
    (destination: TransferDestination) => {
      trackActionInvoked("bulk-move", {
        objectKind: "multi-selection",
        selectionCount: items.length,
      });
      void (async () => {
        let failed = 0;
        // Folder cascades need the FULL current tree — fetch it rather than
        // trusting whatever the hook happened to have cached at render time.
        let allRecords = workspaceFileRecords;
        if (workspaceId) {
          try {
            allRecords = await queryClient.fetchQuery({
              ...workspaceTreeQuery(workspaceId),
              staleTime: 5_000,
            });
          } catch {
            // fall back to the render-time snapshot
          }
        }
        for (const item of items) {
          try {
            if (item.kind === "canvas") {
              const canvas = item.data as Canvas;
              await cascade.moveCanvas({
                canvasId: canvas.id,
                parentPath: destination.folderPath,
                workspaceId:
                  destination.workspaceId !== canvas.workspace_id
                    ? destination.workspaceId
                    : undefined,
              });
            } else if (item.kind === "file" || item.kind === "folder") {
              const record = item.data as FileRecord;
              const isFolder = record.path.endsWith("/");
              const leaf = leafNameFromPath(record.path);
              await cascade.moveRecordCascading({
                record,
                payload: {
                  destination: {
                    class: "workspace",
                    workspace_id: destination.workspaceId,
                    path: `${destination.folderPath}${leaf}${isFolder ? "/" : ""}`,
                    is_folder: isFolder,
                  },
                },
                allRecords,
                moveOne: (vars) => moveFileRecord.mutateAsync(vars),
              });
            }
          } catch {
            failed += 1;
          }
        }
        if (failed === 0) {
          toast.success(`Moved ${items.length} to ${destination.label}`);
        } else {
          toast.error(`${failed} of ${items.length} failed to move.`);
        }
        onMoved?.();
      })();
    },
    [
      cascade,
      items,
      moveFileRecord,
      onMoved,
      queryClient,
      workspaceFileRecords,
      workspaceId,
    ],
  );

  // The disabled "current location" row needs the selection's real parent.
  // Items can sit in different folders (a bulk selection is not one row), so
  // only a parent shared by EVERY item disables its destination; mixed or
  // unresolvable parents disable nothing — a same-place move is a harmless
  // no-op, a silently dead menu row is not.
  const currentFolderPath = useMemo(() => {
    const parentOf = (path: string): string => {
      const trimmed = path.endsWith("/") ? path.slice(0, -1) : path;
      const idx = trimmed.lastIndexOf("/");
      return idx === -1 ? "" : trimmed.slice(0, idx + 1);
    };
    const parents = new Set<string>();
    for (const item of items) {
      if (item.kind === "file" || item.kind === "folder") {
        parents.add(parentOf((item.data as FileRecord).path));
      } else if (item.kind === "canvas") {
        const canvasId = (item.data as Canvas).id;
        const row = workspaceFileRecords.find(
          (r) => r.kind === "canvas" && r.canvas?.id === canvasId,
        );
        parents.add(row ? parentOf(row.path) : "\u0000unknown");
      }
    }
    return parents.size === 1 ? [...parents][0] : "\u0000mixed";
  }, [items, workspaceFileRecords]);

  return useMemo(() => {
    if (!hasBulkMove || items.length === 0) return actions;
    if (items.some((i) => i.kind === "chat")) return actions;
    const submenu: ObjectActionDestinationSubmenu = {
      kind: "workspace-destination",
      operation: "move",
      commit,
      currentWorkspaceId: workspaceId,
      currentFolderPath,
      // Folders in the selection can't move into themselves.
      forbiddenPathPrefixes: items
        .filter((i) => i.kind === "folder")
        .map((i) => (i.data as FileRecord).path),
      writableWorkspaceIds,
    };
    return actions.map((action) =>
      action.id === "bulk-move" ? { ...action, submenu } : action,
    );
  }, [
    actions,
    commit,
    currentFolderPath,
    hasBulkMove,
    items,
    workspaceId,
    writableWorkspaceIds,
  ]);
}
